window.PCXVisualiserEngine = {
  sheetWidths: [700, 900, 980, 1050, 1220, 1600, 2100],
  sheetLengths: [2000, 2500, 3000, 4000, 6000],
  productCache: {},

  productHandles: {
    sheets: {
      "10mm": {
  Clear: "10mm-corotherm-multiwall-polycarbonate-roofing-sheet-clear",
  Bronze: "10mm-corotherm-multiwall-polycarbonate-roofing-sheet-bronze",
  Opal: null
},
      "16mm": {
        Clear: "16mm-corotherm-multiwall-polycarbonate-roofing-sheet-clear",
        Bronze: "16mm-corotherm-multiwall-polycarbonate-roofing-sheet-bronze",
        Opal: "16mm-corotherm-multiwall-polycarbonate-roofing-sheet-opal"
      },
      "25mm": {
        Clear: "25mm-corotherm-multiwall-polycarbonate-roofing-sheet-clear",
        Bronze: "25mm-corotherm-multiwall-polycarbonate-roofing-sheet-bronze",
        Opal: "25mm-corotherm-multiwall-polycarbonate-roofing-sheet-opal-1"
      }
    },

    glazingBar: "corotherm-multiwall-polycarbonate-sheet-glazing-bar-with-1-end-cap-1",
    glazingEndCap: "corotherm-multiwall-polycarbonate-sheet-glazing-bar-end-cap",
    sideFlashing: "corotherm-multiwall-polycarbonate-sheet-side-flashing",
    endClosure: "corotherm-multiwall-polycarbonate-sheet-end-closure-2-1m",
    fixings: "corotherm-multiwall-polycarbonate-sheet-super-fixings-buttons-pack-of-10",
    breather: "corotherm-multiwall-polycarbonate-sheet-breather-tape-10m",
    wallFlashing: "corotherm-wall-flashing-for-polycarbonate-225mm-x-10m"
  },

  normaliseText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/anthracite/g, "grey")
      .replace(/gray/g, "grey")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  },

  formatMoney(pence) {
    return "£" + (Number(pence || 0) / 100).toFixed(2);
  },

  getSheetWidth(width, sheetCount) {
    const target = Number(width) / Number(sheetCount);

    for (const sheetWidth of this.sheetWidths) {
      if (sheetWidth >= target) return sheetWidth;
    }

    return this.sheetWidths[this.sheetWidths.length - 1];
  },

  getSheetLength(projection) {
    const projectionMm = Number(projection);

    for (const length of this.sheetLengths) {
      if (length >= projectionMm) return length;
    }

    return this.sheetLengths[this.sheetLengths.length - 1];
  },

  chooseBarLength(projection) {
    const projectionMm = Number(projection);

    if (projectionMm <= 2000) return 2;
    if (projectionMm <= 3000) return 3;
    if (projectionMm <= 4000) return 4;
    return 6;
  },

  async fetchProduct(handle) {
  if (!handle) {
    throw new Error('A product handle was not provided.');
  }

  /*
   * Cache the request promise immediately.
   * This prevents the three thickness calculations from requesting
   * the same Shopify product several times simultaneously.
   */
  if (this.productCache[handle]) {
    return this.productCache[handle];
  }

  const loadProduct = async (attempt = 1) => {
    try {
      const response = await fetch(`/products/${handle}.js`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(
          `Shopify returned ${response.status} for product: ${handle}`
        );
      }

      return await response.json();

    } catch (error) {
      /*
       * Retry temporary Shopify/network failures twice.
       */
      if (attempt < 3) {
        await new Promise((resolve) => {
          setTimeout(resolve, attempt * 500);
        });

        return loadProduct(attempt + 1);
      }

      throw error;
    }
  };

  this.productCache[handle] = loadProduct();

  try {
    return await this.productCache[handle];

  } catch (error) {
    /*
     * Remove a failed request from the cache so a later attempt
     * can try loading the product again.
     */
    delete this.productCache[handle];
    throw error;
  }
},

  variantMatches(variant, requiredTerms) {
    const title = this.normaliseText(variant?.title);

    return requiredTerms.every((term) => {
      return title.includes(this.normaliseText(term));
    });
  },

  findVariant(product, requiredTerms) {
    if (!product || !product.variants) return null;

    return product.variants.find((variant) => {
      return this.variantMatches(variant, requiredTerms);
    }) || null;
  },

  findSheetVariant(product, sheetWidth, requiredLength) {
  if (!product || !Array.isArray(product.variants)) return null;

  const widthText = String(sheetWidth);
  const requiredLengthMm = Number(requiredLength);

  const suitableLengths = this.sheetLengths
    .map(Number)
    .filter((length) => length >= requiredLengthMm)
    .sort((a, b) => a - b);

  for (const candidateLength of suitableLengths) {
    const lengthText = String(candidateLength);

    const matchingVariant = product.variants.find((variant) => {
      const title = this.normaliseText(variant.title);

      return (
        title.includes(widthText) &&
        title.includes(lengthText) &&
        variant.available !== false
      );
    });

    if (matchingVariant) {
      return matchingVariant;
    }
  }

  return null;
},

  findBreatherVariant(product, thickness) {
    if (!product || !product.variants) return null;

    if (thickness === "10mm" || thickness === "16mm") {
      return product.variants.find((variant) => {
        const title = this.normaliseText(variant.title);
        return title.includes("10") && title.includes("16");
      }) || null;
    }

    return product.variants.find((variant) => {
      return this.normaliseText(variant.title).includes("25");
    }) || null;
  },

  findSideFlashingVariant(product, trimColour, thickness, requiredLengthMetres) {
  if (!product || !Array.isArray(product.variants)) return null;

  const requiredLengthMm = Number(requiredLengthMetres) * 1000;

  const availableLengths = [2, 3, 4, 6]
    .filter((length) => length >= Number(requiredLengthMetres))
    .sort((a, b) => a - b);

  for (const candidateLength of availableLengths) {
    const candidateLengthMm = candidateLength * 1000;

    const matchingVariant = product.variants.find((variant) => {
      const title = this.normaliseText(variant.title);

      const matchesColour =
        title.includes(this.normaliseText(trimColour));

      const matchesThickness =
        title.includes(this.normaliseText(thickness));

      const matchesLength =
        title.includes(`${candidateLength}m`) ||
        title.includes(`${candidateLength} metre`) ||
        title.includes(`${candidateLength} meter`) ||
        title.includes(String(candidateLengthMm));

      return (
        matchesColour &&
        matchesThickness &&
        matchesLength &&
        variant.available !== false
      );
    });

    if (matchingVariant) {
      return matchingVariant;
    }
  }

  return null;
},

  variantPricePence(variant) {
    return Number(variant?.price || 0);
  },

  lineTotalPence(variant, quantity) {
    return this.variantPricePence(variant) * Number(quantity || 0);
  },

  calculateBasicKit(input) {
    const width = Number(input.width || 0);
    const projection = Number(input.projection || 0);
    const sheetCount = Number(input.sheetCount || 0);

    const sheetWidth = this.getSheetWidth(width, sheetCount);
    const sheetLength = this.getSheetLength(projection);

    const roofType = input.roofSupport === "Attached to a wall"
      ? "attached"
      : "freestanding";

    const glazingBarCount = Math.max(sheetCount - 1, 0);
    const glazingBarLength = this.chooseBarLength(projection);

    const glazingEndCapCount = roofType === "freestanding"
      ? glazingBarCount
      : 0;

    const sideFlashingCount = 2;
    const sideFlashingLength = this.chooseBarLength(projection);

    let endClosureCount = Math.ceil(width / 2100);
    if (roofType === "freestanding") endClosureCount *= 2;

    const fixingPacks = sheetCount;
    const totalSheetCoverage = sheetWidth * sheetCount;

    let breatherRolls = Math.max(1, Math.ceil(totalSheetCoverage / 10000));
    if (roofType === "freestanding") breatherRolls *= 2;

    const wallFlashingRolls = roofType === "attached"
      ? Math.max(1, Math.ceil(width / 10000))
      : 0;

    return {
      roofType,
      width,
      projection,
      sheetCount,
      sheetWidth,
      sheetLength,
      totalSheetCoverage,
      glazingBarCount,
      glazingBarLength,
      glazingEndCapCount,
      sideFlashingCount,
      sideFlashingLength,
      endClosureCount,
      fixingPacks,
      breatherRolls,
      wallFlashingRolls
    };
  },

  buildQuoteItems(input, thickness, kit, products) {
    const trimColour = input.trimColour === "Grey" ? "Anthracite" : input.trimColour;

    const sheetVariant = this.findSheetVariant(
      products.sheet,
      kit.sheetWidth,
      kit.sheetLength
    );

    const glazingVariant = this.findVariant(products.glazingBar, [
      trimColour,
      `${kit.glazingBarLength}m`
    ]);

    const glazingEndCapVariant = products.glazingEndCap
      ? this.findVariant(products.glazingEndCap, [trimColour])
      : null;

    const sideFlashingVariant = this.findSideFlashingVariant(
  products.sideFlashing,
  trimColour,
  thickness,
  kit.sideFlashingLength
);

    const endClosureVariant = this.findVariant(products.endClosure, [
      trimColour,
      thickness
    ]);

    const fixingsVariant = this.findVariant(products.fixings, [
      trimColour,
      thickness
    ]);

    const breatherVariant = this.findBreatherVariant(products.breather, thickness);

    const wallFlashingVariant = products.wallFlashing
      ? products.wallFlashing.variants?.[0]
      : null;

    return {
      sheetVariant,
      glazingVariant,
      glazingEndCapVariant,
      sideFlashingVariant,
      endClosureVariant,
      fixingsVariant,
      breatherVariant,
      wallFlashingVariant
    };
  },

  async buildQuoteForThickness(input, thickness) {
    const kit = this.calculateBasicKit(input);
    const handles = this.productHandles;

    const sheetHandle = handles.sheets[thickness]?.[input.sheetColour];

    if (!sheetHandle) {
      throw new Error(`${thickness} ${input.sheetColour} sheets are not available.`);
    }

    const products = {
      sheet: await this.fetchProduct(sheetHandle),
      glazingBar: await this.fetchProduct(handles.glazingBar),
      sideFlashing: await this.fetchProduct(handles.sideFlashing),
      endClosure: await this.fetchProduct(handles.endClosure),
      fixings: await this.fetchProduct(handles.fixings),
      breather: await this.fetchProduct(handles.breather),
      glazingEndCap: kit.roofType === "freestanding"
        ? await this.fetchProduct(handles.glazingEndCap)
        : null,
      wallFlashing: kit.roofType === "attached"
        ? await this.fetchProduct(handles.wallFlashing)
        : null
    };

    const variants = this.buildQuoteItems(input, thickness, kit, products);

if (!variants.sheetVariant) {
  throw new Error(
    `No suitable ${thickness} ${input.sheetColour} polycarbonate sheet could be found for ` +
    `${kit.sheetWidth}mm width and at least ${kit.sheetLength}mm length.`
  );
}

const selectedSheetTitle = this.normaliseText(variants.sheetVariant.title);

const actualSheetLength = this.sheetLengths
  .map(Number)
  .filter((length) => selectedSheetTitle.includes(String(length)))
  .sort((a, b) => b - a)[0];

if (actualSheetLength) {
  kit.sheetLength = actualSheetLength;
}

const lines = [
      { label: "Sheets", variant: variants.sheetVariant, quantity: kit.sheetCount },
      { label: "Glazing bars", variant: variants.glazingVariant, quantity: kit.glazingBarCount },
      { label: "Glazing bar end caps", variant: variants.glazingEndCapVariant, quantity: kit.glazingEndCapCount },
      { label: "Side flashings", variant: variants.sideFlashingVariant, quantity: kit.sideFlashingCount },
      { label: "End closures", variant: variants.endClosureVariant, quantity: kit.endClosureCount },
      { label: "Fixings", variant: variants.fixingsVariant, quantity: kit.fixingPacks },
      { label: "Breather tape", variant: variants.breatherVariant, quantity: kit.breatherRolls },
      { label: "Wall flashing", variant: variants.wallFlashingVariant, quantity: kit.wallFlashingRolls }
    ].filter((line) => line.quantity > 0);

    const missing = lines.filter((line) => !line.variant);

if (missing.length > 0) {
  const missingLabels = missing
    .map((line) => line.label)
    .join(", ");

  throw new Error(
    `The ${thickness} roof kit could not be completed because these required products were not found: ${missingLabels}.`
  );
}

const totalPence = lines.reduce((sum, line) => {
      return sum + this.lineTotalPence(line.variant, line.quantity);
    }, 0);

    const cartItems = lines
      .filter((line) => line.variant)
      .map((line) => ({
        id: line.variant.id,
        quantity: line.quantity
      }));

    return {
      thickness,
      kit,
      products,
      variants,
      lines,
      missing,
      cartItems,
      totalPence,
      totalFormatted: this.formatMoney(totalPence)
    };
  },

  async buildAllQuotes(input) {
  const thicknesses = ["10mm", "16mm", "25mm"];

  const results = await Promise.allSettled(
    thicknesses.map((thickness) => {
      return this.buildQuoteForThickness(input, thickness);
    })
  );

  return results
    .filter((result) => result.status === "fulfilled")
    .map((result) => result.value);
}
};